// Loading and progress indicators: Spinner (ProgressView tinted orange, like LoadingOverlay), Skeleton placeholders
// for lists and tiles while the replica loads, and ProgressBar (first-load progress, goal progress).

import { LoaderCircle } from 'lucide-react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { cn } from './cn';
import { normalizeHex } from './color';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';

const SPINNER_SIZE: Record<SpinnerSize, string> = {
  xs: 'size-3.5',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-8',
};

export interface SpinnerProps {
  size?: SpinnerSize;
  /** Accessible name. Omit (or pass null) when the surrounding control already says it is busy (aria-busy). */
  label?: string | null;
  className?: string;
}

/** Indeterminate activity indicator. Inherits `currentColor`; `text-primary` gives the iOS orange tint. */
export function Spinner({ size = 'sm', label = 'Loading', className }: SpinnerProps) {
  if (label === null) {
    return <LoaderCircle aria-hidden className={cn('shrink-0 animate-spin', SPINNER_SIZE[size], className)} />;
  }
  return (
    <span role="status" className={cn('inline-flex shrink-0', className)}>
      <LoaderCircle aria-hidden className={cn('animate-spin', SPINNER_SIZE[size])} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Width/height as CSS lengths, for when a Tailwind class is not convenient (e.g. random-ish text widths). */
  width?: number | string;
  height?: number | string;
  /** Round (avatars, presence dots). */
  circle?: boolean;
}

/** A pulsing placeholder block. Hidden from assistive tech: put `aria-busy` on the region that is loading. */
export function Skeleton({ width, height, circle, className, style, ...rest }: SkeletonProps) {
  const sizing: CSSProperties = {
    ...(width !== undefined ? { width } : null),
    ...(height !== undefined ? { height } : null),
    ...style,
  };
  return (
    <div
      aria-hidden
      {...rest}
      style={sizing}
      className={cn(
        'animate-shimmer bg-surface-3',
        circle ? 'rounded-full' : 'rounded-chip',
        // Default height only when the caller sets none (there is no tailwind-merge to let a later h-* win).
        height === undefined && !/(^|\s)(h|size)-/.test(className ?? '') && 'h-4',
        className,
      )}
    />
  );
}

/** A few lines of text-like skeletons, the last one shorter (list rows, card bodies). */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div aria-hidden className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className="h-3.5" width={i === lines - 1 && lines > 1 ? '60%' : '100%'} />
      ))}
    </div>
  );
}

export type ProgressTone = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'navy';

const PROGRESS_FILL: Record<ProgressTone, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  navy: 'bg-navy',
};

export interface ProgressBarProps {
  /** 0…1. Omit (or pass null) for an indeterminate bar. Values outside the range are clamped. */
  value?: number | null;
  /** Accessible name ("Loading stores", "Goal progress"). */
  label: string;
  /** Shows the label (and the percentage when determinate) above the bar. */
  showLabel?: boolean;
  /** Extra text after the percentage, e.g. "1,240 rows". */
  detail?: string;
  tone?: ProgressTone;
  /** A user-chosen hex fill (goal or stage color) instead of a tone. */
  color?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function ProgressBar({
  value,
  label,
  showLabel = false,
  detail,
  tone = 'primary',
  color,
  size = 'sm',
  className,
}: ProgressBarProps) {
  const determinate = typeof value === 'number' && Number.isFinite(value);
  const clamped = determinate ? Math.min(1, Math.max(0, value)) : 0;
  const percent = Math.round(clamped * 100);
  // User-chosen hexes go through the iOS parsing rules (an unparseable value draws gray, not an invisible bar).
  const fillStyle: CSSProperties | undefined = color ? { backgroundColor: normalizeHex(color) } : undefined;
  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div className="mb-1.5 flex items-baseline justify-between gap-3 text-xs">
          <span className="font-medium text-fg">{label}</span>
          <span className="text-fg-muted tabular-nums">
            {determinate ? `${percent}%` : ''}
            {detail ? `${determinate ? ' · ' : ''}${detail}` : ''}
          </span>
        </div>
      )}
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={determinate ? 0 : undefined}
        aria-valuemax={determinate ? 100 : undefined}
        aria-valuenow={determinate ? percent : undefined}
        aria-valuetext={detail ? `${determinate ? `${percent}%, ` : ''}${detail}` : undefined}
        className={cn('relative w-full overflow-hidden rounded-full bg-surface-3', size === 'sm' ? 'h-1.5' : 'h-2.5')}
      >
        {determinate ? (
          <div
            className={cn('h-full rounded-full transition-[width] duration-300 ease-out', !color && PROGRESS_FILL[tone])}
            style={{ width: `${percent}%`, ...fillStyle }}
          />
        ) : (
          <div
            className={cn('absolute inset-y-0 w-2/5 animate-indeterminate rounded-full', !color && PROGRESS_FILL[tone])}
            style={fillStyle}
          />
        )}
      </div>
    </div>
  );
}
